import { IPagination } from '../../../types/pagination'
import { calculateProjectOfferTotalWithRebate } from '../../../utils/calculations'
import { AccountingActions } from '../actions/accountingActions'
import * as constants from '../actions/constants'
import { IPassThroughCost } from '../types/directAndPassTroughCosts'
import { IDirectCostInvoice, IPositionPayment, IProjectInvoice } from '../types/invoice'
import { IProject } from '../types/project'
import { ProjectActions } from './../actions/projectActions'
import { IProjectBudgetCost } from './../types/costs'
import { IDirectCostPositionPayment } from './../types/invoice'

export const _initialInvoice: IProjectInvoice = {
  id: '',
  number: '',
  date: '',
  description: '',
  cost_category_name: '',
  cost_category_id: '',
  payments: [],
  type: 'incoming'
}

export const _initialPositionPayment: IPositionPayment = {
  id: '',
  invoice_id: '',
  invoice_date: '',
  invoice_number: '',
  invoice: _initialInvoice,
  cost: {} as IProjectBudgetCost,
  spt_external_cost: {} as IProjectBudgetCost,
  payment_amount: '',
  fulfillment: '',
  pass_through_cost: {} as IPassThroughCost,
  invoice_passed_through: null
}

export const _initialPaginatedPayments: IPagination<IPositionPayment[]> = {
  links: { next: '', previous: '' },
  count: 0,
  current_page: 0,
  total_pages: 0,
  results: [],
  page_size: 5,
  last_page: 0,
  to: 0,
  from: 0
}

export const _initialPaginatedInvoices: IPagination<IProjectInvoice[]> = {
  links: { next: '', previous: '' },
  count: 0,
  current_page: 0,
  total_pages: 0,
  results: [],
  page_size: 5,
  last_page: 0,
  to: 0,
  from: 0
}

export const _initialDirectCostInvoice: IDirectCostInvoice = {
  id: '',
  number: '',
  description: '',
  presumed_date: '',
  invoiced_on: '',
  paid_on: '',
  invoiced_amount: 0,
  type: 'outgoing',
  billing_procedure: 'scheduled_payment',
  payments: [] as IDirectCostPositionPayment[],
  direct_cost_total: 0,
  percent_of_total: 0
}

export const projectInvoices = (state: IProjectInvoice[] = [], action: AccountingActions): IProjectInvoice[] => {
  switch (action.type) {
    case constants.GET_INVOICES_SUCCESS:
      return action.invoices

    case constants.CREATE_INVOICE_SUCCESS:
      return [...state, action.invoice]

    case constants.UPDATE_INVOICE_SUCCESS:
      return state.map(a => {
        if (+a.id === +action.invoice.id) {
          return action.invoice
        }
        return a
      })

    case constants.DELETE_INVOICE_SUCCESS:
      return state.filter(i => i.id !== action.invoice.id)

    default:
      return state
  }
}


export const projectInvoice = (state: IProjectInvoice = _initialInvoice, action: AccountingActions): IProjectInvoice => {
  switch (action.type) {
    case constants.GET_INVOICE_SUCCESS:
      return action.invoice

    default:
      return state
  }
}

export const selectedInvoice = (state: IProjectInvoice = _initialInvoice, action: AccountingActions): IProjectInvoice => {
  switch (action.type) {
    case constants.SET_SELECTED_INVOICE:
    case constants.CREATE_INVOICE_SUCCESS:
    case constants.UPDATE_INVOICE_SUCCESS:
    case constants.GET_INVOICE_SUCCESS:
      return action.invoice

    case constants.DELETE_INVOICE_SUCCESS:
      return _initialInvoice

    default:
      return state
  }
}

export const sortInvoices = (state: { name: string, direction: string} = { name: '', direction: ''}, action: AccountingActions): { name: string, direction: string } => {
  switch (action.type) {
    case constants.SORT_INVOICES:
      return action.sort

    default:
      return state
  }
}

export const paginatedInvoices = (state: IPagination<IProjectInvoice[]> = _initialPaginatedInvoices, action: AccountingActions): IPagination<IProjectInvoice[]> => {
  switch (action.type) {
    case constants.GET_PAGINATED_INVOICES_SUCCESS:
      return action.data

    case constants.CREATE_INVOICE_SUCCESS:
      return {...state, results: [...state.results, action.invoice]}

    case constants.UPDATE_INVOICE_SUCCESS: {
      const invoices = state.results.map(a => {
        if (+a.id === +action.invoice.id) {
          return action.invoice
        }
        return a
      })

      return {...state, results: invoices}
    }

    case constants.DELETE_INVOICE_SUCCESS:
      return {...state, results: state.results.filter(i => i.id !== action.invoice.id)}

    default:
      return state
  }
}


export const positionPayments = (state: IPagination<IPositionPayment[]> = _initialPaginatedPayments, action: AccountingActions): IPagination<IPositionPayment[]> => {
  switch (action.type) {
    case constants.GET_PAGINATED_PAYMENTS_SUCCESS:
      return action.data

    default:
      return state
  }
}

export const nonPaginatedPayments = (state: IPositionPayment[] = [], action: AccountingActions): IPositionPayment[] => {
  switch (action.type) {
    case constants.GET_PAYMENTS_SUCCESS:
      return action.data

    default:
      return state
  }
}

export const sortPayments = (state: { name: string, direction: string} = { name: '', direction: ''}, action: AccountingActions): { name: string, direction: string } => {
  switch (action.type) {
    case constants.SORT_PAYMENTS:
      return action.sort

    default:
      return state
  }
}

export const paginatedPaymentsActivePage = (state = 1, action: AccountingActions | ProjectActions): number => {
  switch (action.type) {
    case constants.SET_PAGINATED_PAYMENTS_ACTIVE_PAGE:
      return action.page

    // reset page to 1 after project change
    case constants.GET_PROJECT_SUCCESS:
      return 1

    default:
      return state
  }
}

export const accountingTab = (state = '', action: AccountingActions): string => {
  switch (action.type) {
    case constants.SELECT_ACCOUTING_TAB:
      return action.tab

    default:
      return state
  }
}

export const paginatedInvoiceActivePage = (state = 1, action: AccountingActions | ProjectActions): number => {
  switch (action.type) {
    case constants.SET_PAGINATED_INVOICES_ACTIVE_PAGE:
      return action.page

    // reset page to 1 after project change
    case constants.GET_PROJECT_SUCCESS:
      return 1

    default:
      return state
  }
}

export const calculateInvoicedAmount = (invoice: IDirectCostInvoice) => {
  let invoiced_amount = +invoice.invoiced_amount
  if (invoice.billing_procedure === 'billing_by_tasks' && invoice.payments) {
    // Seeded with 0: an empty array is truthy, so it passes the guard above and an unseeded
    // reduce throws "Reduce of empty array with no initial value" — which would take down the
    // whole accounting table, not just this row.
    invoiced_amount = +invoice.payments.map(p => p.payment_amount).reduce((sum, num) => +sum + +num, 0)
  }
  return invoiced_amount
}

/**
 * A milestone's share of the project's rebated study total, in percent (issue #120).
 *
 * The client specified this formula verbatim: `milestone total * 100 / rebated study total`,
 * where "milestone total" is the amount as ENTERED — the rebate is never applied to an invoice.
 * Only the denominator carries it. That asymmetry is the whole point: the rebate shrinks the
 * study, so each unchanged milestone becomes a larger share of it.
 *
 *   55.059,90 / 373.961,00 = 14,7%   without the rebate
 *   55.059,90 / 299.168,80 = 18,4%   with a 20% rebate
 *
 * An earlier version rebated both sides, so the factor cancelled and the column read 14,7%
 * whatever the rebate was — the fix was a no-op. Never reintroduce a rebate on the numerator.
 *
 * The denominator is the whole study's offer value, NOT the sum of the schedule, so this column
 * does not normalise: a schedule billing more or less than the study total sums to over or under
 * 100%, which the client confirmed is expected.
 *
 * Annotated in the selector rather than the reducer because the study total lives on the project,
 * which a slice reducer cannot reach, and because the "% of Total" column is sortable — the sort
 * reads percent_of_total off the same annotated list.
 *
 * @param invoice - the milestone invoice; billing-aware via calculateInvoicedAmount
 * @param project - supplies financial_calculation.total_budget and the rebate
 * @returns the share in percent, or 0 when the study total is 0
 * @example
 * // study total 373961 with a 20% rebate -> 299168.80; milestone 55059.90
 * milestoneSharePercent(invoice, project) // 18.4043...
 */
export const milestoneSharePercent = (invoice: IDirectCostInvoice, project: IProject): number => {
  const studyTotal = calculateProjectOfferTotalWithRebate(project)
  return studyTotal !== 0 ? (calculateInvoicedAmount(invoice) / studyTotal) * 100 : 0
}

/* Scheduled Payments or Billing By Taks*/
export const directCostInvoices = (state: IDirectCostInvoice[] = [], action: AccountingActions): IDirectCostInvoice[] => {
  switch (action.type) {
    case constants.GET_DIRECT_COST_INVOICES_SUCCESS:
      return action.invoices

    case constants.CREATE_DIRECT_COST_INVOICE_SUCCESS:
      return [...state, action.invoice]

    case constants.UPDATE_DIRECT_COST_INVOICE_SUCCESS:
      return state.map(a => (+a.id === +action.invoice.id ? action.invoice : a))

    case constants.DELETE_DIRECT_COST_INVOICE_SUCCESS:
      return state.filter(i => i.id !== action.invoice.id)

    default:
      return state
  }
}


export const directCostInvoice = (state: IDirectCostInvoice = _initialDirectCostInvoice, action: AccountingActions): IDirectCostInvoice => {
  switch (action.type) {
    case constants.GET_DIRECT_COST_INVOICE_SUCCESS:
      return action.invoice

    default:
      return state
  }
}

export const selectedDirectCostInvoice = (state: IDirectCostInvoice = _initialDirectCostInvoice, action: AccountingActions): IDirectCostInvoice => {
  switch (action.type) {
    case constants.SET_SELECTED_DIRECT_COST_INVOICE:
    case constants.CREATE_DIRECT_COST_INVOICE_SUCCESS:
    case constants.UPDATE_DIRECT_COST_INVOICE_SUCCESS:
    case constants.GET_DIRECT_COST_INVOICE_SUCCESS:
      return {...action.invoice, percent_of_total: +action.invoice.invoiced_amount !== 0 && +action.invoice.direct_cost_total !== 0 ? ((+action.invoice.invoiced_amount / +action.invoice.direct_cost_total) * 100) : 0}

    case constants.DELETE_DIRECT_COST_INVOICE_SUCCESS:
      return _initialDirectCostInvoice

    default:
      return state
  }
}

export const sortDirectCostInvoices = (state: { name: string, direction: string} = { name: '', direction: ''}, action: AccountingActions): { name: string, direction: string } => {
  switch (action.type) {
    case constants.SORT_DIRECT_COST_INVOICES:
      return action.sort

    default:
      return state
  }
}

export const latestPayments = (state: IProjectInvoice[] = [], action: AccountingActions): IProjectInvoice[] => {
  switch (action.type) {
    case constants.GET_LATEST_PAYMENTS_SUCCESS:
      return action.invoices

    default:
      return state
  }
}

export const sortPtc = (state: { name: string, direction: string} = { name: '', direction: ''}, action: AccountingActions): { name: string, direction: string } => {
  switch (action.type) {
    case constants.SORT_PTC:
      return action.sort

    default:
      return state
  }
}
